import {
  AMOUNT_MAX,
  AMOUNT_MIN,
  CREATED_OPTIONS,
  EXPIRY_OPTIONS,
  FilterOption,
  FundingFilterState,
  SEARCH_FIELD_OPTIONS,
  SORT_SECTIONS,
  STATUS_OPTIONS,
  UPDATED_OPTIONS,
} from './fundingFilters'

/**
 * URL round-trip for the funding requests filters, mirroring the CoSell pipeline's
 * `parseCosellPipelineFiltersFromUrl` / `buildCosellPipelineSearchParams` pair so the
 * page is reconstructible from its address: a refresh, a shared link or the back
 * button all land on the same filtered table.
 *
 * Unknown values are dropped rather than trusted — a hand-edited URL must not put the
 * table into a state the filter panel cannot represent.
 */

export const FUNDING_FILTER_PARAM_KEYS = {
  createdWithin: 'created_within',
  updatedWithin: 'updated_within',
  expiryWithin: 'expiry_within',
  programNames: 'program',
  statuses: 'status',
  amountMin: 'amount_min',
  amountMax: 'amount_max',
  sort: 'sort',
  search: 'search',
  searchBy: 'search_by',
} as const

const ALL_PRESET = 'all'

const parseCommaSeparated = (value: string | null): string[] =>
  value
    ? value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    : []

const pickValidKeys = (values: string[], options: FilterOption[]): string[] => {
  const allowed = new Set(options.map(option => option.key))
  return values.filter(value => allowed.has(value))
}

/** A relative preset is only honoured when it is one of the panel's own options. */
const parsePreset = (value: string | null, options: FilterOption[]): string => {
  if (!value) return ALL_PRESET
  return options.some(option => option.key === value) ? value : ALL_PRESET
}

/**
 * Clamp a bound into the slider's range. A non-numeric or out-of-range value falls
 * back to the range edge, so a bad URL widens the filter rather than hiding rows.
 */
const parseAmountBound = (value: string | null, fallback: number): number => {
  if (value === null || value.trim() === '') return fallback
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(AMOUNT_MAX, Math.max(AMOUNT_MIN, parsed))
}

const parseAmount = (params: URLSearchParams): [number, number] => {
  const min = parseAmountBound(
    params.get(FUNDING_FILTER_PARAM_KEYS.amountMin),
    AMOUNT_MIN
  )
  const max = parseAmountBound(
    params.get(FUNDING_FILTER_PARAM_KEYS.amountMax),
    AMOUNT_MAX
  )
  // An inverted range would match nothing; treat it as unset.
  return min > max ? [AMOUNT_MIN, AMOUNT_MAX] : [min, max]
}

export const parseFundingFiltersFromUrl = (
  search: string
): FundingFilterState => {
  const params = new URLSearchParams(search)
  return {
    createdWithin: parsePreset(
      params.get(FUNDING_FILTER_PARAM_KEYS.createdWithin),
      CREATED_OPTIONS
    ),
    updatedWithin: parsePreset(
      params.get(FUNDING_FILTER_PARAM_KEYS.updatedWithin),
      UPDATED_OPTIONS
    ),
    expiryWithin: parsePreset(
      params.get(FUNDING_FILTER_PARAM_KEYS.expiryWithin),
      EXPIRY_OPTIONS
    ),
    // The program list loads from the API, so a name cannot be checked here; an
    // unknown one is ignored by the endpoint and leaves the table unfiltered.
    programNames: parseCommaSeparated(
      params.get(FUNDING_FILTER_PARAM_KEYS.programNames)
    ),
    statuses: pickValidKeys(
      parseCommaSeparated(params.get(FUNDING_FILTER_PARAM_KEYS.statuses)),
      STATUS_OPTIONS
    ),
    amount: parseAmount(params),
    search: params.get(FUNDING_FILTER_PARAM_KEYS.search)?.trim() ?? '',
    searchBy:
      SEARCH_FIELD_OPTIONS.find(
        option => option.key === params.get(FUNDING_FILTER_PARAM_KEYS.searchBy)
      )?.key ?? SEARCH_FIELD_OPTIONS[0].key,
  }
}

const SORT_KEYS = new Set(
  SORT_SECTIONS.flatMap(section => section.options.map(option => option.key))
)

export const parseFundingSortFromUrl = (search: string): string | undefined => {
  const value = new URLSearchParams(search).get(FUNDING_FILTER_PARAM_KEYS.sort)
  return value && SORT_KEYS.has(value) ? value : undefined
}

/**
 * Write filters and sort onto a copy of `current`, leaving every unrelated param
 * (notably the `tab` selection) untouched. Defaults are deleted rather than written
 * so an unfiltered table has a clean URL.
 */
export const applyFundingFiltersToParams = (
  current: URLSearchParams,
  filters: FundingFilterState,
  sort?: string
): URLSearchParams => {
  const next = new URLSearchParams(current)

  const setOrDelete = (key: string, value: string | null) => {
    if (value) next.set(key, value)
    else next.delete(key)
  }

  const presetValue = (preset: string) =>
    preset === ALL_PRESET ? null : preset

  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.createdWithin,
    presetValue(filters.createdWithin)
  )
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.updatedWithin,
    presetValue(filters.updatedWithin)
  )
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.expiryWithin,
    presetValue(filters.expiryWithin)
  )
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.programNames,
    filters.programNames.join(',')
  )
  setOrDelete(FUNDING_FILTER_PARAM_KEYS.statuses, filters.statuses.join(','))
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.amountMin,
    filters.amount[0] > AMOUNT_MIN ? String(filters.amount[0]) : null
  )
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.amountMax,
    filters.amount[1] < AMOUNT_MAX ? String(filters.amount[1]) : null
  )
  setOrDelete(FUNDING_FILTER_PARAM_KEYS.sort, sort ?? null)
  const search = filters.search.trim()
  setOrDelete(FUNDING_FILTER_PARAM_KEYS.search, search || null)
  setOrDelete(
    FUNDING_FILTER_PARAM_KEYS.searchBy,
    search ? filters.searchBy : null
  )

  return next
}
